// ============================================================================
// REGISTRATIONS REGISTER SCREEN
// ============================================================================
// The firm-wide register of client statutory registrations — one row per
// registration, across every client.
//
// WHY THIS IS A SCREEN AND NOT A FILTER ON THE CLIENT GRID
//
// It answers two questions the client grid structurally cannot:
//
//   1. "Whose GSTIN/PAN is this?"  — paste the number, see the row AND the
//      client. A client-grain filter would return a client row without saying
//      WHICH registration matched, which for a client holding five state
//      GSTINs is exactly the thing you wanted to know.
//   2. "Which clients hold an IE Code?" — filter by type, read off client +
//      number + expiry together, export it.
//
// Both are registration-grain questions. See ADR-0018.
//
// IT SHARES THE CLIENT MASTER'S SCREEN CODE
//
// `client-registration` was already granted by fix 161 for the per-client
// drill-down; it was simply absent from the sidebar. Surfacing it here means
// one permission governs both the register and the editor, with no new grant
// and no migration. Editing opens the very same ClientRegistrationFormModal
// the drill-down uses, so a wrong number found by reverse lookup is fixable
// where it was found.

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { exportToExcel } from 'utils';
import {
  useClientRegistrations,
  useUpdateClientRegistration,
  useDeleteClientRegistration,
} from 'hooks/api/useClientRegistration';
import { useRegistrationTypes } from 'hooks/api/useRegistrationType';
import { useCountries } from 'hooks/api/useCountry';
import { clientRegistrationService } from 'api/services/clientRegistrationService';
import type {
  ClientRegistration,
  ClientRegistrationSearchRequest,
  UpdateClientRegistrationRequest,
} from 'api/types';
import ClientRegistrationFormModal, {
  type ClientRegistrationFormData,
} from 'components/modals/modules/client/ClientRegistrationFormModal';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

// Maps to the API's two expiry params. "Due in N" deliberately includes rows
// already expired — a watchlist that hides what is overdue is the wrong one.
const EXPIRY_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: '30', label: 'Due within 30 days' },
  { value: '60', label: 'Due within 60 days' },
  { value: '90', label: 'Due within 90 days' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

const toDateInput = (iso?: string | null): string => (iso && iso.length >= 10 ? iso.slice(0, 10) : '');

const apiErrorMessage = (err: any, fallback: string): string => {
  const apiBody = err?.response?.data;
  if (typeof apiBody?.message === 'string' && apiBody.message) return apiBody.message;
  if (Array.isArray(apiBody?.errors) && apiBody.errors.length) return apiBody.errors.join(', ');
  if (apiBody?.errors && typeof apiBody.errors === 'object') {
    return Object.values(apiBody.errors).flat().join(', ');
  }
  return err?.message || fallback;
};

interface ClientRegistrationRegisterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const columnDefinitions: ColumnDefinition[] = [
  { id: 'client',       label: 'Client',       field: 'clientName',            required: true,  defaultVisible: true,  sortable: true },
  { id: 'type',         label: 'Type',         field: 'registrationTypeCode',  required: true,  defaultVisible: true,  sortable: true },
  { id: 'number',       label: 'Number',       field: 'registrationNumber',    required: true,  defaultVisible: true,  sortable: true },
  { id: 'jurisdiction', label: 'Jurisdiction', field: 'jurisdiction',          required: false, defaultVisible: true,  sortable: false },
  { id: 'country',      label: 'Country',      field: 'countryName',           required: false, defaultVisible: false, sortable: false },
  { id: 'validTo',      label: 'Valid To',     field: 'validTo',               required: false, defaultVisible: true,  sortable: true },
  { id: 'status',       label: 'Status',       field: 'statusName',            required: false, defaultVisible: true,  sortable: true },
  { id: 'updatedAt',    label: 'Updated',      field: 'updatedAt',             required: false, defaultVisible: false, sortable: true },
];

const COLUMN_PREF_KEY = 'client-registration-register-columns';

const ClientRegistrationRegisterScreen: React.FC<ClientRegistrationRegisterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  // ---- column visibility ------------------------------------------------
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id)
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // ---- query state ------------------------------------------------------
  // No clientId: this is the cross-client register.
  const [queryParams, setQueryParams] = useState<ClientRegistrationSearchRequest>({
    page: 1,
    pageSize: 25,
    includeInactive: true,
  });
  const { data, pagination, loading, refetch } = useClientRegistrations(queryParams);

  const typeParams = useMemo(() => ({ page: 1, pageSize: 200, includeInactive: true }), []);
  const { data: registrationTypes } = useRegistrationTypes(typeParams);
  const { data: countries } = useCountries();

  // Only active types are offered when EDITING a registration; the filter
  // dropdown lists retired ones too, because registrations recorded against
  // them still exist and still need finding.
  const activeRegistrationTypes = useMemo(
    () => registrationTypes.filter((t) => t.statusCode === 'ACTIVE'),
    [registrationTypes]
  );

  const typeFilterOptions = useMemo(
    () => [
      { value: '', label: 'All types' },
      ...registrationTypes.map((t) => ({
        value: t.code,
        label: `${t.countryCode ?? 'Any'} · ${t.code} — ${t.name}`,
      })),
    ],
    [registrationTypes]
  );

  const countryFilterOptions = useMemo(
    () => [
      { value: '', label: 'All countries' },
      ...(countries ?? []).map((c: any) => ({
        value: c.countryCode,
        label: `${c.countryCode} — ${c.countryName}`,
      })),
    ],
    [countries]
  );

  // ---- view state -------------------------------------------------------
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [typeFilter, setTypeFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.registrationTypeCode) count++;
    if (queryParams.countryCode) count++;
    if (queryParams.expiredOnly || queryParams.expiringWithinDays) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [
    queryParams.registrationTypeCode,
    queryParams.countryCode,
    queryParams.expiredOnly,
    queryParams.expiringWithinDays,
    queryParams.statusCode,
  ]);

  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      registrationTypeCode: typeFilter || undefined,
      countryCode: countryFilter || undefined,
      expiredOnly: expiryFilter === 'EXPIRED' ? true : undefined,
      expiringWithinDays:
        expiryFilter && expiryFilter !== 'EXPIRED' ? Number(expiryFilter) : undefined,
      statusCode: statusFilter || undefined,
      includeInactive: true,
      page: 1,
    }));
    setFilterOpen(false);
  }, [typeFilter, countryFilter, expiryFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setTypeFilter('');
    setCountryFilter('');
    setExpiryFilter('');
    setStatusFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      includeInactive: true,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  // ---- edit / delete ----------------------------------------------------
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<ClientRegistration | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formApiError, setFormApiError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<ClientRegistration | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { updateClientRegistration } = useUpdateClientRegistration();
  const { deleteClientRegistration } = useDeleteClientRegistration();

  const handleEdit = useCallback((row: ClientRegistration) => {
    setSelected(row);
    setFormApiError(null);
    setFormOpen(true);
  }, []);

  const formInitialData: ClientRegistrationFormData | null = useMemo(() => {
    if (!selected) return null;
    return {
      registrationTypeId: String(selected.registrationTypeId),
      registrationNumber: selected.registrationNumber,
      jurisdiction: selected.jurisdiction ?? '',
      registrationDate: toDateInput(selected.registrationDate),
      validFrom: toDateInput(selected.validFrom),
      validTo: toDateInput(selected.validTo),
      renewalReminderDays:
        selected.renewalReminderDays === null || selected.renewalReminderDays === undefined
          ? ''
          : String(selected.renewalReminderDays),
      issuingAuthority: selected.issuingAuthority ?? '',
      remarks: selected.remarks ?? '',
      statusCode: (selected.statusCode as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE',
    };
  }, [selected]);

  const handleSaveForm = useCallback(
    async (formData: ClientRegistrationFormData) => {
      if (!selected) return;
      setIsSaving(true);
      setFormApiError(null);
      try {
        const payload: UpdateClientRegistrationRequest = {
          registrationTypeId: formData.registrationTypeId,
          registrationNumber: formData.registrationNumber,
          jurisdiction: formData.jurisdiction || undefined,
          registrationDate: formData.registrationDate || null,
          validFrom: formData.validFrom || null,
          validTo: formData.validTo || null,
          renewalReminderDays: formData.renewalReminderDays
            ? Number(formData.renewalReminderDays)
            : null,
          issuingAuthority: formData.issuingAuthority || undefined,
          remarks: formData.remarks || undefined,
          statusCode: formData.statusCode,
          rowVersion: selected.rowVersion,
        };
        await updateClientRegistration(selected.id, payload);
        notificationService.toast.success(`Registration "${payload.registrationNumber}" updated.`);
        setIsSaving(false);
        setFormOpen(false);
        setSelected(null);
        refetch();
      } catch (err: any) {
        setIsSaving(false);
        const message = apiErrorMessage(err, 'Failed to save the registration. Please try again.');
        setFormApiError(message);
        notificationService.toast.error(message, 6000);
      }
    },
    [selected, updateClientRegistration, refetch]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteClientRegistration(deleteTarget.id);
      notificationService.toast.success(`Registration "${deleteTarget.registrationNumber}" deleted.`);
      setIsDeleting(false);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(apiErrorMessage(err, 'Failed to delete the registration.'), 6000);
    }
  }, [deleteTarget, deleteClientRegistration, refetch]);

  // ---- export -----------------------------------------------------------
  const [isExporting, setIsExporting] = useState(false);

  /**
   * Exports the FILTERED SET, not the visible page. The grid is server-paged,
   * so exporting `data` would hand over 25 rows of a 400-row answer — the sort
   * of quiet wrongness that only surfaces after someone has acted on the file.
   *
   * It pages through rather than asking for one huge page, because
   * QueryParameters clamps PageSize to 500 SILENTLY: a single request for
   * "everything" would come back truncated with no error, which is the same
   * trap one level up. EXPORT_PAGE_CAP bounds the loop so a mis-set filter
   * cannot walk the whole table; going over it warns rather than pretending.
   */
  const handleExport = useCallback(async () => {
    const EXPORT_PAGE_SIZE = 500; // the server's own maximum
    const EXPORT_PAGE_CAP = 10;   // 5,000 rows

    setIsExporting(true);
    try {
      const rows: ClientRegistration[] = [];
      let page = 1;
      let totalItems = 0;

      for (; page <= EXPORT_PAGE_CAP; page += 1) {
        const response = await clientRegistrationService.getAll({
          ...queryParams,
          page,
          pageSize: EXPORT_PAGE_SIZE,
        });
        rows.push(...response.data);
        totalItems = response.pagination.totalItems;
        if (!response.pagination.hasNextPage || response.data.length === 0) break;
      }

      if (rows.length === 0) {
        notificationService.toast.info('Nothing to export for the current filters.');
        return;
      }
      if (totalItems > rows.length) {
        notificationService.toast.warning(
          `Exporting ${rows.length} of ${totalItems} registrations — the export caps at ${EXPORT_PAGE_SIZE * EXPORT_PAGE_CAP} rows. Narrow the filters to get the rest.`,
          8000
        );
      }
      exportToExcel({
        data: rows.map((r) => ({
          client: r.clientName ?? '',
          clientCode: r.clientCode ?? '',
          type: r.registrationTypeCode,
          typeName: r.registrationTypeName,
          number: r.registrationNumber,
          jurisdiction: r.jurisdiction ?? '',
          country: r.countryName ?? '',
          issuingAuthority: r.issuingAuthority ?? '',
          registrationDate: r.registrationDate ? formatDate(r.registrationDate) : '',
          validFrom: r.validFrom ? formatDate(r.validFrom) : '',
          validTo: r.validTo ? formatDate(r.validTo) : 'Evergreen',
          status: r.statusName,
        })),
        fileName: `registrations-${new Date().toISOString().slice(0, 10)}.xlsx`,
        headerMap: {
          client: 'Client',
          clientCode: 'Client Code',
          type: 'Type',
          typeName: 'Type Name',
          number: 'Registration Number',
          jurisdiction: 'Jurisdiction',
          country: 'Country',
          issuingAuthority: 'Issuing Authority',
          registrationDate: 'Registration Date',
          validFrom: 'Valid From',
          validTo: 'Valid To',
          status: 'Status',
        },
      });
    } catch (err: any) {
      notificationService.toast.error(apiErrorMessage(err, 'Export failed.'), 6000);
    } finally {
      setIsExporting(false);
    }
  }, [queryParams]);

  // ---- grid -------------------------------------------------------------
  const tableColumns: SyutGridColumn<ClientRegistration>[] = useMemo(
    () => [
      {
        id: 'client',
        header: 'Client',
        accessorKey: 'clientName',
        enableSorting: true,
        cell: ({ row }) => (
          <span title={row.original.clientCode ?? undefined}>{row.original.clientName || '-'}</span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'registrationTypeCode',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => (
          <span title={row.original.registrationTypeName}>{row.original.registrationTypeCode}</span>
        ),
      },
      {
        id: 'number',
        header: 'Number',
        accessorKey: 'registrationNumber',
        size: 200,
        enableSorting: true,
        cell: ({ row }) => (
          <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>
            {row.original.registrationNumber}
          </span>
        ),
      },
      {
        id: 'jurisdiction',
        header: 'Jurisdiction',
        accessorKey: 'jurisdiction',
        size: 150,
        enableSorting: false,
        cell: ({ row }) => row.original.jurisdiction || '-',
      },
      {
        id: 'country',
        header: 'Country',
        accessorKey: 'countryName',
        size: 150,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.countryCode
            ? `${row.original.countryCode} — ${row.original.countryName ?? ''}`.trim()
            : 'Any',
      },
      {
        id: 'validTo',
        header: 'Valid To',
        accessorKey: 'validTo',
        size: 150,
        enableSorting: true,
        cell: ({ row }) => {
          const { validTo, daysToExpiry } = row.original;
          if (!validTo) return <span style={{ color: 'var(--text-secondary)' }}>Evergreen</span>;
          const expired = typeof daysToExpiry === 'number' && daysToExpiry < 0;
          const dueSoon = typeof daysToExpiry === 'number' && daysToExpiry >= 0 && daysToExpiry <= 30;
          return (
            <span style={{ color: expired ? '#dc2626' : dueSoon ? '#d97706' : undefined }}>
              {formatDate(validTo)}
              {expired
                ? ' (expired)'
                : dueSoon
                  ? ` (${daysToExpiry}d)`
                  : ''}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => {
          const isActive = row.original.statusCode === 'ACTIVE';
          return (
            <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
              {row.original.statusName || (isActive ? 'Active' : 'Inactive')}
            </span>
          );
        },
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessorKey: 'updatedAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    []
  );

  const visibleTableColumns = useMemo<SyutGridColumn<ClientRegistration>[]>(
    () => tableColumns.filter((col) => visibleColumns.includes(col.id as string)),
    [tableColumns, visibleColumns]
  );

  const getRowActions = useCallback(
    (row: ClientRegistration): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];
      if (allowedActions.includes('edit')) {
        items.push({
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={16} />,
          onClick: () => handleEdit(row),
        });
      }
      if (allowedActions.includes('delete')) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => setDeleteTarget(row),
          variant: 'danger',
        });
      }
      return items;
    },
    [allowedActions, handleEdit]
  );

  const actionButtons: ActionButton[] = useMemo(
    () => [
      {
        id: 'export',
        icon: <IconSystem.download size={18} />,
        label: 'Export',
        tooltip: 'Export the filtered registrations to Excel',
        onClick: handleExport,
        variant: 'icon' as const,
        disabled: isExporting || loading,
      },
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [handleExport, isExporting, loading, refetch]
  );

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Registrations"
        description="Every statutory registration across clients — search a number to find its owner, or filter by type."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.file size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Paste a GSTIN / PAN, or search client, type, jurisdiction…',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={visibleTableColumns}
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              enableSorting
              rowActions={
                allowedActions.includes('edit') || allowedActions.includes('delete')
                  ? getRowActions
                  : undefined
              }
              emptyMessage={
                loading
                  ? 'Loading registrations…'
                  : searchQuery
                    ? `No registration matches “${searchQuery}”.`
                    : 'No registrations found for the current filters.'
              }
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Registration Type"
                  options={typeFilterOptions}
                  value={typeFilter}
                  onChange={(val) => setTypeFilter((val as string) || '')}
                  isClearable={false}
                  isSearchable
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Country"
                  options={countryFilterOptions}
                  value={countryFilter}
                  onChange={(val) => setCountryFilter((val as string) || '')}
                  isClearable={false}
                  isSearchable
                  size="sm"
                />
                <div style={{ marginTop: '0.25rem', fontSize: '0.6875rem', color: 'var(--text-secondary)' }}>
                  Types that apply everywhere are always included
                </div>
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Expiry"
                  options={EXPIRY_FILTER_OPTIONS}
                  value={expiryFilter}
                  onChange={(val) => setExpiryFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
                <div style={{ marginTop: '0.25rem', fontSize: '0.6875rem', color: 'var(--text-secondary)' }}>
                  Evergreen registrations are excluded; “due within” includes overdue
                </div>
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <ClientRegistrationFormModal
        isOpen={formOpen}
        mode="edit"
        clientName={selected?.clientName ?? null}
        registrationTypes={activeRegistrationTypes}
        initialData={formInitialData}
        isSaving={isSaving}
        apiError={formApiError}
        onSave={handleSaveForm}
        onClose={() => {
          setFormOpen(false);
          setSelected(null);
          setFormApiError(null);
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Registration"
        message={
          deleteTarget
            ? `Delete ${deleteTarget.registrationTypeCode} "${deleteTarget.registrationNumber}" held by ${deleteTarget.clientName}? Anything already filed against it keeps its history, but it stops being offered for new work.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) setDeleteTarget(null);
        }}
      />
    </div>
  );
};

export default ClientRegistrationRegisterScreen;
